"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Form = void 0;
const Field_1 = require("./Field");
const FormEvents_1 = require("./FormEvents");
/**
 * A form composed of {@link Field} instances
 *
 * Provides methods to access all values, errors, and other field properties,
 * as well as manages form submissions
 *
 * @class
 */
class Form {
    /**
     * Creates a new form
     * @param initialValues Initial values to use on the form, can include additional data
     *  (fields not present on form) to provide back on submission
     * @param fieldArrays Field arrays to manage
     * @param fieldValidation Dictionary or lookup function to get field validation methods
     * @param formValidation Method to validate all fields
     * @param onInitialize Callback when the form is initialized
     * @param onSubmitValid Callback when all fields are valid, provides field values
     * @param onSubmitError Callback when one or more fields have errors, provides field errors
     * @param onReset Callback when the form is reset
     */
    constructor({ initialValues, fieldValidation, formValidation, onInitialize, onSubmitValid, onSubmitError, onReset, fieldArrays, }) {
        // callbacks
        /** Provided callback function for valid form submissions */
        this._onSubmitValid = () => { };
        /** Provided callback function for form submissions with at least one error */
        this._onSubmitError = () => { };
        /** Provided callback function used on form reset */
        this._onReset = () => { };
        // internal
        /** FormEvents instance shared by this form and all fields */
        this._events = new FormEvents_1.FormEvents();
        /** All fields on this form */
        this._fields = {};
        /** Whether the form is currently submitting */
        this._submitting = false;
        this._initialValues = initialValues || {};
        this._fieldValidation = fieldValidation || {};
        this._formValidation = formValidation || (() => ({}));
        this._fieldArrays = fieldArrays || [];
        // create fields for all initial data
        Object.entries(this._initialValues).map(([fieldName, initialValue]) => {
            const field = new Field_1.Field(fieldName, initialValue, this);
            this.addField(field);
        });
        // initialize field arrays with field names
        const fieldNames = this.getAllFieldNames();
        this._fieldArrays.forEach((fieldArray) => {
            fieldArray.initializeFieldArray(fieldNames);
        });
        // call initialization callback
        if (onInitialize) {
            onInitialize(this);
        }
        // set form callbacks
        this.setOnSubmitValid(onSubmitValid);
        this.setOnSubmitError(onSubmitError);
        this.setOnReset(onReset);
    }
    //////////////////////////////////////////////////////////////////////////////
    // callback management
    //////////////////////////////////////////////////////////////////////////////
    /**
     * Sets the onSubmitValid callback
     * Since callbacks don't have initialization side effects, we can safely redefine them as needed
     * @param onSubmitValid Callback when all fields are valid, provides field values
     */
    setOnSubmitValid(onSubmitValid) {
        if (onSubmitValid !== undefined) {
            this._onSubmitValid = onSubmitValid;
        }
        else {
            this._onSubmitValid = () => { };
        }
    }
    /**
     * Sets the onSubmitError callback
     * Since callbacks don't have initialization side effects, we can safely redefine them as needed
     * @param onSubmitError Callback when one or more fields have errors, provides field errors
     */
    setOnSubmitError(onSubmitError) {
        if (onSubmitError !== undefined) {
            this._onSubmitError = onSubmitError;
        }
        else {
            this._onSubmitError = () => { };
        }
    }
    /**
     * Sets the onReset callback
     * Since callbacks don't have initialization side effects, we can safely redefine them as needed
     * @param onReset Callback when the form is reset
     */
    setOnReset(onReset) {
        if (onReset !== undefined) {
            this._onReset = onReset;
        }
        else {
            this._onReset = () => { };
        }
    }
    //////////////////////////////////////////////////////////////////////////////
    // field management
    //////////////////////////////////////////////////////////////////////////////
    /** All field names */
    getAllFieldNames() {
        return Object.keys(this._fields);
    }
    /** Whether a field exists on the form */
    hasField(fieldName) {
        return this.getAllFieldNames().includes(fieldName);
    }
    /** The matching field for a given name */
    getField(fieldName) {
        if (!this.hasField(fieldName)) {
            throw new Error(`Field "${fieldName}" does not exist!`);
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return this._fields[fieldName];
    }
    /** The provided initial value for a field */
    getInitialValue(fieldName) {
        return this._initialValues[fieldName];
    }
    /** Form-provided validation functions for a field */
    getFieldValidationFunctions(fieldName) {
        let validation;
        if (typeof this._fieldValidation === "function") {
            validation = this._fieldValidation(fieldName);
        }
        else {
            validation = this._fieldValidation[fieldName] || [];
        }
        if (!Array.isArray(validation)) {
            return [validation];
        }
        return validation;
    }
    /** Adds a field to the form */
    addField(field) {
        const fieldName = field.getName();
        if (this.hasField(fieldName)) {
            throw new Error(`A field named "${fieldName}" already exists on this form!`);
        }
        this._fields[fieldName] = field;
    }
    /** Removes the matching field from the form */
    removeField(fieldName) {
        if (!this.hasField(fieldName)) {
            throw new Error(`Tried to remove field "${fieldName}", but it does not exist!`);
        }
        delete this._fields[fieldName];
    }
    /** Adds a field array to the form */
    addFieldArray(fieldArray) {
        fieldArray.initializeFieldArray(this.getAllFieldNames());
        this._fieldArrays.push(fieldArray);
    }
    removeFieldArray(fieldArray) {
        this._fieldArrays = this._fieldArrays.filter((fa) => fa !== fieldArray);
    }
    //////////////////////////////////////////////////////////////////////////////
    // getters
    //////////////////////////////////////////////////////////////////////////////
    /** All field values by name */
    getValues() {
        const fieldEntries = Object.entries(this._fields);
        const valueEntries = fieldEntries.map(([fieldName, field]) => [fieldName, field.getValue()]);
        return Object.fromEntries(valueEntries);
    }
    /** All field errors by field name (errored fields only) */
    getErrors() {
        const fieldEntries = Object.entries(this._fields);
        const erroredFieldEntries = fieldEntries.filter(([, field]) => field.hasError());
        const fieldErrorEntries = erroredFieldEntries.map(([fieldName, field]) => [
            fieldName,
            field.getErrorOrNull(),
        ]); // nulls removed by previous filter
        return Object.fromEntries(fieldErrorEntries);
    }
    /** Names of all fields that have been touched */
    getTouchedFieldNames() {
        const fieldEntries = Object.entries(this._fields);
        const touchedEntries = fieldEntries.filter(([, field]) => field.isTouched());
        return touchedEntries.map((entries) => entries[0]);
    }
    //////////////////////////////////////////////////////////////////////////////
    // current state
    //////////////////////////////////////////////////////////////////////////////
    /** Whether any field has an error */
    hasErrors() {
        return Object.values(this._fields).some((field) => field.hasError());
    }
    /** Whether any field is currently validating */
    isValidating() {
        return Object.values(this._fields).some((field) => field.isValidating());
    }
    /** Whether any field is dirty */
    isDirty() {
        return Object.values(this._fields).some((field) => field.isDirty());
    }
    /** Whether the form is currently submitting */
    isSubmitting() {
        return this._submitting;
    }
    //////////////////////////////////////////////////////////////////////////////
    // actions
    //////////////////////////////////////////////////////////////////////////////
    /**
     * Validates all fields and then calls form validation
     *
     * Prefer field errors (e.g. invalid input) over form errors
     */
    validate(useFieldValidationCache = true) {
        return __awaiter(this, void 0, void 0, function* () {
            // validate all fields
            yield Promise.all(Object.values(this._fields).map((field) => field.validate(useFieldValidationCache)));
            // validate the form
            const errors = yield this._formValidation(this.getValues(), this);
            Object.entries(errors).forEach(([fieldName, error]) => {
                if (!this.hasField(fieldName)) {
                    throw new Error(`FormValidation tried to set an error for field "${fieldName}", but it does not exist!`);
                }
                const field = this.getField(fieldName);
                // don't overwrite -- prefer field errors (e.g. invalid input) over form errors
                if (!field.hasError()) {
                    field.setError(error);
                }
            });
        });
    }
    /** Clears all field errors */
    clearErrors() {
        Object.values(this._fields).forEach((field) => field.clearError());
    }
    /** Resets the form */
    reset() {
        Object.values(this._fields).forEach((field) => field.reset());
        this._fieldArrays.forEach((fieldArray) => {
            fieldArray.reset();
        });
        this._onReset(this);
    }
    /** Validates all fields and calls {@link Form#_onSubmitValid} or {@link Form#_onSubmitError} */
    submit() {
        return __awaiter(this, void 0, void 0, function* () {
            // onSubmit() is a function that might be passed around, e.g. onClick={form.onSubmit}
            // provide a more descriptive error for the missing closure
            if (!this) {
                throw new Error("onSubmit() is unbound, try (() => form.onSubmit())");
            }
            // prevent duplicate submissions
            if (this._submitting) {
                return;
            }
            // start submission
            this._setSubmitting(true);
            // validate all fields
            yield this.validate();
            // call success or error handler
            if (this.hasErrors()) {
                const errors = this.getErrors();
                yield this._onSubmitError(errors, this);
            }
            else {
                let values = this.getValues();
                // remap field arrays
                // NOTE: we need to remap child keys first, which always have longer keys than their parents
                this._fieldArrays
                    .sort((a, b) => b.fieldPrefix.length - a.fieldPrefix.length)
                    .forEach((fieldArray) => {
                    values = fieldArray.remapArrayIndices(values);
                });
                yield this._onSubmitValid(values, this);
            }
            // finish submission
            this._setSubmitting(false);
        });
    }
    /** Sets "submitting" status and announces form event */
    _setSubmitting(submitting) {
        if (this._submitting !== submitting) {
            this._submitting = submitting;
            this.announceFormUpdate();
        }
    }
    //////////////////////////////////////////////////////////////////////////////
    // event methods
    //////////////////////////////////////////////////////////////////////////////
    /**
     * Wrapper for announcing form updates
     * @borrows FormEvents#updateFormWatchers as announceFormUpdate
     */
    announceFormUpdate() {
        this._events.updateFormWatchers();
    }
    /**
     * Wrapper for announcing field events; also announces a form event
     * @borrows FormEvents#updateFieldWatchers
     * @borrows FormEvents#updateFormWatchers
     */
    announceFieldUpdate(fieldName) {
        // force field updates to be async
        setTimeout(() => {
            this._events.updateFieldWatchers(fieldName);
            this._events.updateFormWatchers(); // also update form
        }, 0);
    }
    /**
     * Wrapper for redrawing on form events
     * @borrows FormEvents#watchFormEvents as watchFormEvents
     */
    watchFormEvents() {
        this._events.watchFormEvents();
    }
    /**
     * Wrapper for redrawing on field events
     * @borrows FormEvents#watchFieldEvents as watchFieldEvents
     */
    watchFieldEvents(fieldName) {
        this._events.watchFieldEvents(fieldName);
    }
}
exports.Form = Form;
